/**
 * Ticket + User module permissions (role matrix: Manager / Team leader / Agent).
 * Other app areas are not keyed here.
 *
 * admin: all keys below. viewer / support: aligned with agent for ticket UX unless changed later.
 */

export const PERMISSIONS = Object.freeze({
  // Ticket — lists & core
  TICKET_LIST_ALL: "ticket.list.all",
  TICKET_LIST_ASSIGNED: "ticket.list.assigned",
  TICKET_LIST_UNASSIGNED: "ticket.list.unassigned",
  TICKET_LIST_JUNK: "ticket.list.junk",
  /** Merge-source tickets (rows merged into another); audit / lookup */
  TICKET_LIST_MERGED: "ticket.list.merged",
  TICKET_EMAIL_COMPOSE: "ticket.email.compose",
  TICKET_CREATE_MANUAL: "ticket.create.manual",
  TICKET_LIST_OWN: "ticket.list.own",
  TICKET_SETTINGS_VIEW: "ticket.settings.view",
  TICKET_SMTP_VIEW: "ticket.smtp.view",
  TICKET_MAILBOXES_VIEW: "ticket.mailboxes.view",
  TICKET_CATEGORIES_VIEW: "ticket.categories.view",
  TICKET_REPLY_TEMPLATES_VIEW: "ticket.reply_templates.view",
  TICKET_ACK_VIEW: "ticket.acknowledgement.view",
  TICKET_FOLLOWUP_TEMPLATES_VIEW: "ticket.followup_templates.view",
  TICKET_EMAIL_FILTERS_VIEW: "ticket.email_filters.view",
  CONFIGURATION_SETTINGS_VIEW: "configuration.settings.view",
  TICKET_SMTP_MANAGE: "ticket.smtp.manage",
  TICKET_MAILBOXES_MANAGE: "ticket.mailboxes.manage",
  TICKET_CATEGORIES_MANAGE: "ticket.categories.manage",
  TICKET_REPLY_TEMPLATES_MANAGE: "ticket.reply_templates.manage",
  TICKET_ACK_MANAGE: "ticket.acknowledgement.manage",
  TICKET_FOLLOWUP_TEMPLATES_MANAGE: "ticket.followup_templates.manage",
  TICKET_EMAIL_FILTERS_MANAGE: "ticket.email_filters.manage",
  CONFIGURATION_SETTINGS_MANAGE: "configuration.settings.manage",
  TICKET_MERGE: "ticket.merge",
  TICKET_BULK_ASSIGN_AUTO: "ticket.bulk_assign.auto",
  TICKET_BULK_ASSIGN_MANUAL: "ticket.bulk_assign.manual",
  TICKET_JUNK_MOVE: "ticket.junk.move",
  TICKET_BULK_REASSIGN: "ticket.bulk.reassign",
  TICKET_BULK_UNASSIGN: "ticket.bulk.unassign",
  TICKET_JUNK_RESTORE: "ticket.junk.restore",
  TICKET_JUNK_SELF_ASSIGN: "ticket.junk.self_assign",
  TICKET_MERGE_CUSTOMER: "ticket.merge.customer",
  TICKET_STATUS_CHANGE: "ticket.status.change",
  TICKET_ACTIVITY_VIEW: "ticket.activity.view",
  TICKET_ASSIGNMENT_HISTORY_VIEW: "ticket.assignment_history.view",
  TICKET_NOTES_ADD: "ticket.notes.add",
  TICKET_REPORTS_CUSTOMIZED: "ticket.reports.customized",
  TICKET_CSAT_EMAILS_VIEW: "ticket.csat_emails.view",
  // User
  USER_LIST: "user.list",
  USER_CREATE: "user.create",
  USER_UPDATE: "user.update",
  USER_DELETE: "user.delete",
});

const ALL_KEYS = Object.freeze(Object.values(PERMISSIONS));

/** Manager column (full ticket + user per matrix) */
const MANAGER = new Set([
  PERMISSIONS.TICKET_LIST_ALL,
  PERMISSIONS.TICKET_LIST_ASSIGNED,
  PERMISSIONS.TICKET_LIST_UNASSIGNED,
  PERMISSIONS.TICKET_LIST_JUNK,
  PERMISSIONS.TICKET_LIST_MERGED,
  PERMISSIONS.TICKET_EMAIL_COMPOSE,
  PERMISSIONS.TICKET_CREATE_MANUAL,
  PERMISSIONS.TICKET_LIST_OWN,
  PERMISSIONS.TICKET_SETTINGS_VIEW,
  PERMISSIONS.TICKET_SMTP_VIEW,
  PERMISSIONS.TICKET_MAILBOXES_VIEW,
  PERMISSIONS.TICKET_CATEGORIES_VIEW,
  PERMISSIONS.TICKET_REPLY_TEMPLATES_VIEW,
  PERMISSIONS.TICKET_ACK_VIEW,
  PERMISSIONS.TICKET_FOLLOWUP_TEMPLATES_VIEW,
  PERMISSIONS.TICKET_EMAIL_FILTERS_VIEW,
  PERMISSIONS.CONFIGURATION_SETTINGS_VIEW,
  PERMISSIONS.TICKET_SMTP_MANAGE,
  PERMISSIONS.TICKET_MAILBOXES_MANAGE,
  PERMISSIONS.TICKET_CATEGORIES_MANAGE,
  PERMISSIONS.TICKET_REPLY_TEMPLATES_MANAGE,
  PERMISSIONS.TICKET_ACK_MANAGE,
  PERMISSIONS.TICKET_FOLLOWUP_TEMPLATES_MANAGE,
  PERMISSIONS.TICKET_EMAIL_FILTERS_MANAGE,
  PERMISSIONS.CONFIGURATION_SETTINGS_MANAGE,
  PERMISSIONS.TICKET_MERGE,
  PERMISSIONS.TICKET_BULK_ASSIGN_AUTO,
  PERMISSIONS.TICKET_BULK_ASSIGN_MANUAL,
  PERMISSIONS.TICKET_JUNK_MOVE,
  PERMISSIONS.TICKET_BULK_REASSIGN,
  PERMISSIONS.TICKET_BULK_UNASSIGN,
  PERMISSIONS.TICKET_JUNK_RESTORE,
  PERMISSIONS.TICKET_JUNK_SELF_ASSIGN,
  PERMISSIONS.TICKET_MERGE_CUSTOMER,
  PERMISSIONS.TICKET_STATUS_CHANGE,
  PERMISSIONS.TICKET_ACTIVITY_VIEW,
  PERMISSIONS.TICKET_ASSIGNMENT_HISTORY_VIEW,
  PERMISSIONS.TICKET_NOTES_ADD,
  PERMISSIONS.TICKET_REPORTS_CUSTOMIZED,
  PERMISSIONS.TICKET_CSAT_EMAILS_VIEW,
  PERMISSIONS.USER_LIST,
  PERMISSIONS.USER_CREATE,
  PERMISSIONS.USER_UPDATE,
  PERMISSIONS.USER_DELETE,
]);

/** Team leader column */
const TEAM_LEADER = new Set([
  PERMISSIONS.TICKET_LIST_ALL,
  PERMISSIONS.TICKET_LIST_ASSIGNED,
  PERMISSIONS.TICKET_LIST_UNASSIGNED,
  PERMISSIONS.TICKET_LIST_JUNK,
  PERMISSIONS.TICKET_LIST_MERGED,
  PERMISSIONS.TICKET_EMAIL_COMPOSE,
  PERMISSIONS.TICKET_CREATE_MANUAL,
  PERMISSIONS.TICKET_LIST_OWN,
  PERMISSIONS.TICKET_MAILBOXES_VIEW,
  PERMISSIONS.TICKET_CATEGORIES_VIEW,
  PERMISSIONS.TICKET_REPLY_TEMPLATES_VIEW,
  PERMISSIONS.TICKET_ACK_VIEW,
  PERMISSIONS.TICKET_FOLLOWUP_TEMPLATES_VIEW,
  PERMISSIONS.TICKET_EMAIL_FILTERS_VIEW,
  PERMISSIONS.CONFIGURATION_SETTINGS_VIEW,
  PERMISSIONS.TICKET_REPLY_TEMPLATES_MANAGE,
  PERMISSIONS.TICKET_EMAIL_FILTERS_MANAGE,
  PERMISSIONS.CONFIGURATION_SETTINGS_MANAGE,
  PERMISSIONS.TICKET_MERGE,
  PERMISSIONS.TICKET_BULK_ASSIGN_AUTO,
  PERMISSIONS.TICKET_BULK_ASSIGN_MANUAL,
  PERMISSIONS.TICKET_JUNK_MOVE,
  PERMISSIONS.TICKET_BULK_REASSIGN,
  PERMISSIONS.TICKET_BULK_UNASSIGN,
  PERMISSIONS.TICKET_JUNK_RESTORE,
  PERMISSIONS.TICKET_JUNK_SELF_ASSIGN,
  PERMISSIONS.TICKET_MERGE_CUSTOMER,
  PERMISSIONS.TICKET_STATUS_CHANGE,
  PERMISSIONS.TICKET_ACTIVITY_VIEW,
  PERMISSIONS.TICKET_ASSIGNMENT_HISTORY_VIEW,
  PERMISSIONS.TICKET_NOTES_ADD,
  PERMISSIONS.TICKET_REPORTS_CUSTOMIZED,
  PERMISSIONS.TICKET_CSAT_EMAILS_VIEW,
  PERMISSIONS.USER_LIST,
  PERMISSIONS.USER_CREATE,
  PERMISSIONS.USER_UPDATE,
  PERMISSIONS.USER_DELETE,
]);

/** Agent column */
const AGENT = new Set([
  PERMISSIONS.TICKET_LIST_ASSIGNED,
  PERMISSIONS.TICKET_LIST_JUNK,
  PERMISSIONS.TICKET_LIST_MERGED,
  PERMISSIONS.TICKET_EMAIL_COMPOSE,
  PERMISSIONS.TICKET_LIST_OWN,
  PERMISSIONS.TICKET_MAILBOXES_VIEW,
  PERMISSIONS.TICKET_CATEGORIES_VIEW,
  PERMISSIONS.TICKET_REPLY_TEMPLATES_VIEW,
  PERMISSIONS.TICKET_ACK_VIEW,
  PERMISSIONS.TICKET_FOLLOWUP_TEMPLATES_VIEW,
  PERMISSIONS.TICKET_MERGE,
  PERMISSIONS.TICKET_MERGE_CUSTOMER,
  PERMISSIONS.TICKET_STATUS_CHANGE,
  PERMISSIONS.TICKET_NOTES_ADD,
  PERMISSIONS.TICKET_JUNK_MOVE,
  PERMISSIONS.TICKET_JUNK_SELF_ASSIGN,
]);

const ROLE_PERMISSIONS = {
  admin: null, // all PERMISSIONS keys
  manager: MANAGER,
  team_leader: TEAM_LEADER,
  agent: AGENT,
  viewer: AGENT,
  support: AGENT,
};

/**
 * @param {string|null|undefined} role
 * @param {string} permissionKey
 * @returns {boolean}
 */
export function roleHasPermission(role, permissionKey) {
  if (!role || !permissionKey) return false;
  if (role === "admin") return ALL_KEYS.includes(permissionKey);
  const set = ROLE_PERMISSIONS[role];
  if (!set) return false;
  return set.has(permissionKey);
}

/**
 * @param {string|null|undefined} role
 * @param {string[]} keys
 */
export function roleHasAnyPermission(role, keys) {
  if (!keys || !keys.length) return true;
  return keys.some((k) => roleHasPermission(role, k));
}

/** Minimum access to open ticket detail (any list the user may use). */
export const TICKET_DETAIL_ROUTE_PERMISSIONS = Object.freeze([
  PERMISSIONS.TICKET_LIST_ALL,
  PERMISSIONS.TICKET_LIST_ASSIGNED,
  PERMISSIONS.TICKET_LIST_UNASSIGNED,
  PERMISSIONS.TICKET_LIST_JUNK,
  PERMISSIONS.TICKET_LIST_MERGED,
  PERMISSIONS.TICKET_LIST_OWN,
]);
